// Shared SEO helpers.

/**
 * hreflang alternate <link> entries for a canonical Finnish URL.
 *
 * We used to advertise `?lang=en`, `?lang=sv`, `?lang=et` as alternate
 * hreflang targets, but those aren't real localized pages (the UI dict
 * swaps client-side; the served HTML is Finnish). Google indexed them
 * as duplicates and started ranking `/vaaterekki?lang=et` etc. in the
 * Finnish SERP.
 *
 * The clean signal for a Finnish-only page is a self-referencing
 * `hreflang="fi"` plus `x-default` — both pointing at the canonical URL.
 * Query variants keep their canonical pointing here (set at the route
 * level), so Google consolidates them into this one URL.
 */
export function hreflangLinks(url: string) {
  return [
    { rel: "alternate", hrefLang: "fi", href: url },
    { rel: "alternate", hrefLang: "x-default", href: url },
  ];
}
export const SITE_URL = "https://montessori24.fi";

/** Turn a bundled asset path (/assets/x.jpg) into an absolute URL for crawlers. */
export function absoluteUrl(pathOrUrl: string) {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/**
 * Full Open Graph + Twitter Card meta set for a Montessori-koulu article,
 * so shares render the right title, description and image.
 */
export function articleMeta(args: {
  slug: string;
  title: string;
  description: string;
  published: string;
  updated?: string;
  image: string;
  section?: string;
}) {
  const url = `${SITE_URL}/montessori-koulu/${args.slug}`;
  const image = absoluteUrl(args.image);
  return [
    { title: args.title },
    { name: "description", content: args.description },
    { property: "og:title", content: args.title },
    { property: "og:description", content: args.description },
    { property: "og:type", content: "article" },
    { property: "og:url", content: url },
    { property: "og:site_name", content: "Montessori24" },
    { property: "og:locale", content: "fi_FI" },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:alt", content: args.title },
    { property: "article:published_time", content: args.published },
    { property: "article:modified_time", content: args.updated ?? args.published },
    { property: "article:section", content: args.section ?? "Montessori-koulu" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: args.title },
    { name: "twitter:description", content: args.description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: args.title },
  ];
}
